/**
 * Nova Proactive Reach-Out — Trigger Runner
 *
 * Evaluates milestone rules against user journey_timeline, checks fired_triggers,
 * respects 1 message/day limit, prioritizes by type, and handles delivery or queue.
 *
 * Cron: run at 6am UTC. Delivery only 8am–8pm user local time.
 */

import { MILESTONE_TRIGGER_RULES, TRIGGER_TYPES } from '../config/nova-triggers.js';
import { resolveMessage } from '../config/nova-messages.js';

const PRIORITY_ORDER = [
  'PREPARATORY',
  'EMOTIONAL',
  'MILESTONE',
  'PATTERN',
  'SEASONAL',
];

function getTypePriority(type) {
  const i = PRIORITY_ORDER.indexOf(type);
  return i >= 0 ? PRIORITY_ORDER.length - i : 0;
}

function hasFiredToday(firedTriggers, userId) {
  const today = new Date().toISOString().slice(0, 10);
  const key = String(userId || '').toLowerCase();
  const list = Array.isArray(firedTriggers) ? firedTriggers : [];
  return list.some(
    (t) =>
      String(t.user_id || '').toLowerCase() === key &&
      (t.fired_at || '').slice(0, 10) === today
  );
}

function lastFiredForMessage(firedTriggers, userId, messageId) {
  const key = String(userId || '').toLowerCase();
  const list = Array.isArray(firedTriggers) ? firedTriggers : [];
  const matches = list.filter(
    (t) =>
      String(t.user_id || '').toLowerCase() === key &&
      t.message_id === messageId
  );
  if (matches.length === 0) return null;
  return matches.sort((a, b) => new Date(b.fired_at) - new Date(a.fired_at))[0];
}

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return (Date.now() - d) / (1000 * 60 * 60 * 24);
}

function isWithinDeliveryWindow(userTimezone = 'UTC') {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone || 'UTC',
      hour: 'numeric',
    });
    const hour = parseInt(formatter.format(now), 10);
    if (Number.isNaN(hour)) return true;
    return hour >= 8 && hour < 20;
  } catch {
    return true;
  }
}

/**
 * Build evaluation context for a user.
 */
export function buildContext({
  user,
  timeline,
  logs = [],
  lossHistory = [],
}) {
  const moodLogs = logs.filter((l) => l.log_type === 'mood');
  const hotFlashLogs = logs.filter((l) => l.log_type === 'hot_flash');
  return {
    userId: user?.email || user?.id || '',
    user,
    chapter: timeline?.chapter || user?.journeyType || 'cycle',
    timeline,
    moodLogs,
    hotFlashLogs,
    lossHistory,
  };
}

/**
 * Evaluate all rules for a user. Returns eligible candidates sorted by priority.
 */
export function evaluateUserRules(ctx, firedTriggers) {
  const results = [];
  const userId = ctx.userId || '';

  for (const rule of MILESTONE_TRIGGER_RULES) {
    if (rule.chapter !== ctx.chapter) continue;
    if (!rule.condition_fn(ctx)) continue;

    const last = lastFiredForMessage(firedTriggers, userId, rule.message_id);
    if (!rule.is_repeatable && last) continue;
    if (rule.cooldown_days > 0 && last) {
      const since = daysSince(last.fired_at);
      if (since < rule.cooldown_days) continue;
    }

    results.push({
      rule,
      message_id: rule.message_id,
      type: rule.type,
      typePriority: getTypePriority(rule.type),
    });
  }

  return results.sort((a, b) => b.typePriority - a.typePriority);
}

/**
 * Pick at most one message to deliver today (highest priority wins).
 */
export function selectMessageForToday(candidates, firedTriggers, userId) {
  if (hasFiredToday(firedTriggers, userId)) return null;
  return candidates[0] || null;
}

/**
 * Build tokens for message resolution.
 */
export function buildTokens(user, timeline, messageId) {
  const first = (user?.name || '').trim().split(/\s+/)[0] || 'there';
  const due = timeline?.key_dates?.due_date;
  let week_number = '';
  if (due) {
    const dueDate = new Date(due);
    const elapsed = (dueDate - new Date()) / (1000 * 60 * 60 * 24);
    week_number = String(Math.max(1, Math.min(40, Math.floor(40 - elapsed / 7))));
  }
  const child = timeline?.children?.[0];
  const child_name = child?.name || 'your baby';
  let child_age = '';
  if (child?.dob) {
    const months = Math.floor(
      (Date.now() - new Date(child.dob)) / (1000 * 60 * 60 * 24 * 30.44)
    );
    child_age = months < 12 ? `${months} mo` : `${Math.floor(months / 12)} yr`;
  }
  const city = user?.city || '';
  return { first_name: first, week_number, child_name, child_age, city };
}

/**
 * Run trigger evaluation for one user.
 * @param {Object} options
 * @param {Object} options.user
 * @param {Object} options.timeline
 * @param {Array} options.logs
 * @param {Array} options.firedTriggers
 * @param {Array} options.lossHistory
 * @param {string} [options.userTimezone]
 * @param {Function} options.onDeliver - (user, messageId, resolvedMessage) => Promise
 * @param {Function} options.onQueue - (user, messageId, resolvedMessage) => void
 * @param {Function} options.addFiredTrigger - (userId, messageId) => void
 * @returns {{ delivered: boolean, queued: boolean, message_id?: string }}
 */
export function runForUser({
  user,
  timeline,
  logs = [],
  firedTriggers = [],
  lossHistory = [],
  userTimezone = 'UTC',
  onDeliver,
  onQueue,
  addFiredTrigger,
}) {
  const ctx = buildContext({ user, timeline, logs, lossHistory });
  const candidates = evaluateUserRules(ctx, firedTriggers);
  const selected = selectMessageForToday(candidates, firedTriggers, ctx.userId);

  if (!selected) return { delivered: false, queued: false };

  const tokens = buildTokens(user, timeline, selected.message_id);
  const resolved = resolveMessage(selected.message_id, tokens);
  if (!resolved) return { delivered: false, queued: false };

  const inWindow = isWithinDeliveryWindow(userTimezone);

  if (inWindow) {
    addFiredTrigger?.(ctx.userId, selected.message_id);
    onDeliver?.(user, selected.message_id, resolved);
    return { delivered: true, queued: false, message_id: selected.message_id };
  }

  onQueue?.(user, selected.message_id, resolved);
  return { delivered: false, queued: true, message_id: selected.message_id };
}

/**
 * Run for all active users (cron entry point).
 * @param {Object} options
 * @param {Function} options.getActiveUsers - () => Promise<Array<{user, timeline, logs, firedTriggers, lossHistory, timezone}>>
 * @param {Function} options.addFiredTrigger - (userId, messageId) => Promise
 * @param {Function} options.deliverNotification - (user, messageId, message) => Promise
 * @param {Function} options.queueNotification - (user, messageId, message) => void
 */
export async function runDailyCron({
  getActiveUsers,
  addFiredTrigger,
  deliverNotification,
  queueNotification,
}) {
  const users = await getActiveUsers();
  const results = { delivered: 0, queued: 0 };

  for (const row of users) {
    const { user, timeline, logs, firedTriggers, lossHistory, timezone } = row;
    const r = runForUser({
      user,
      timeline: timeline || {},
      logs: logs || [],
      firedTriggers: firedTriggers || [],
      lossHistory: lossHistory || [],
      userTimezone: timezone || 'UTC',
      addFiredTrigger,
      onDeliver: deliverNotification,
      onQueue: queueNotification ? (u, mid, msg) => queueNotification(u, mid, msg) : undefined,
    });
    if (r.delivered) results.delivered++;
    if (r.queued) results.queued++;
  }

  return results;
}
